import { ColoursObj } from "@eventr/common-storybook";
import { useEffect } from "react";
import { titleForPath } from "routing/all-routes";
import { nativeNavigationReact } from "./init";
import { prewarmToolbarIcons } from "./toolbarIcons";

/**
 * Configures each native view as soon as it is created (iOS only).
 *
 * - Scroll lock: each native view is a full-bleed WKWebView whose scroll view
 *   bounces and captures the gesture, so the document overscrolls and the app's
 *   inner scroll container never scrolls (and pull-to-refresh breaks). Locking the
 *   view's html/body (position: fixed, overflow: hidden) removes the document's
 *   scroll range, leaving the inner container as the only scroller. This is applied
 *   per native view, so web and Android are untouched.
 * - Title: set from the route registry so it appears immediately rather than
 *   flashing in after the React view renders (the router integration cannot carry a
 *   title to a pushed view).
 */
export function NativeNavigationViewSetup() {
  useEffect(() => {
    const handle = nativeNavigationReact;
    if (!handle) return;

    // Rasterise the common toolbar icons up front so the first view that uses one
    // already has a PNG ready and its bar button appears with the push.
    prewarmToolbarIcons();

    // Lock THIS view's document immediately. addViewsListener only fires for views
    // created after registration, so the current view (e.g. each tab on launch) never
    // triggers the listener and would otherwise keep the WKWebView UIScrollView
    // active, stealing gestures before the inner overflow:auto container sees them.
    lockViewDocument(document);

    return handle.addViewsListener((view, event) => {
      if (event === "remove") return;

      // Lock each newly-created native view's document so only the inner app
      // container scrolls.
      try {
        lockViewDocument(view.window.document);
      } catch {
        // ignore cross-window access issues
      }

      // Set the native bar title from the route registry.
      const title = view.data.path ? titleForPath(view.data.path) : undefined;
      if (title) {
        handle.plugin.update({ id: view.id, update: { title } }).catch(() => {});
      }
    });
  }, []);

  return null;
}

function lockViewDocument(doc: Document): void {
  doc.documentElement.style.height = "100%";
  doc.documentElement.style.overflow = "hidden";
  doc.body.style.position = "fixed";
  doc.body.style.top = "0";
  doc.body.style.left = "0";
  doc.body.style.right = "0";
  doc.body.style.bottom = "0";
  doc.body.style.overflow = "hidden";
  doc.body.style.overscrollBehavior = "none";
  // Paint each view opaque. Native views stack in a UINavigationController, so a
  // transparent view (e.g. a pushed auth screen with gaps) would otherwise let the view
  // beneath it show through, the welcome screen's background photo bleeding into sign
  // in. A screen with its own background image (welcome) still paints it over this.
  doc.documentElement.style.backgroundColor = ColoursObj.backgrounds.primary;
  doc.body.style.backgroundColor = ColoursObj.backgrounds.primary;
}
